import { useEffect, useMemo, useState } from 'react';
import type { PostgrestError } from '@supabase/supabase-js';
import { useAuth, useSupabase } from '../providers/SupabaseProvider';
import { interpretFeatureFlag } from '../utils/featureAccess';
import { ACCOUNTING_FEATURE_ENABLED } from '../constants/features';

const FEATURE_FALLBACKS: Partial<Record<FeatureAccessKey, FeatureAccessKey>> = {
  feature_qm_manuals: 'feature_management',
  feature_mpu: 'feature_workflow',
};

export type FeatureAccessKey =
  | 'feature_abstinence'
  | 'feature_analytics'
  | 'feature_accounting'
  | 'feature_news'
  | 'feature_calendar'
  | 'feature_advanced_functions'
  | 'feature_qm_manuals'
  | 'feature_file_upload'
  | 'feature_email'
  | 'feature_workflow'
  | 'feature_customizations'
  | 'feature_management'
  | 'feature_mpu'
  | 'feature_personal';

interface FeatureAccessState {
  loading: boolean;
  hasAccess: boolean;
  organizationId: string | null;
  isOwner: boolean;
}

export function useFeatureAccess(featureKey: FeatureAccessKey, providedOrganizationId?: string | null): FeatureAccessState {
  const { user } = useAuth();
  const supabase = useSupabase();
  const [organizationId, setOrganizationId] = useState<string | null>(providedOrganizationId ?? null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const featureDisabled = !ACCOUNTING_FEATURE_ENABLED && featureKey === 'feature_accounting';

  useEffect(() => {
    if (providedOrganizationId) {
      setOrganizationId(providedOrganizationId);
    }
  }, [providedOrganizationId]);

  useEffect(() => {
    let isMounted = true;

    if (featureDisabled) {
      if (isMounted) {
        setOrganizationId(null);
        setLoading(false);
        setHasAccess(false);
        setIsOwner(false);
      }
      return;
    }

    async function resolveOrganizationId() {
      if (!user?.id) {
        if (isMounted) {
          setOrganizationId(null);
          setLoading(false);
          setHasAccess(false);
          setIsOwner(false);
        }
        return;
      }

      if (organizationId) {
        return;
      }

      try {
        const { data, error } = await supabase
          .from('organization_users')
          .select('organization_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) throw error;
        if (isMounted) {
          setOrganizationId(data?.organization_id ?? null);
        }
      } catch (err) {
      console.error('Fehler beim Laden der Organisation für Funktionszugriff:', err);
        if (isMounted) {
          setOrganizationId(null);
        }
      }
    }

    resolveOrganizationId();

    return () => {
      isMounted = false;
    };
  }, [featureDisabled, organizationId, supabase, user?.id]);

  useEffect(() => {
    let isMounted = true;

    if (featureDisabled) {
      if (isMounted) {
        setLoading(false);
        setHasAccess(false);
        setIsOwner(false);
      }
      return;
    }

    async function checkAccess(currentOrganizationId: string | null) {
      if (!user?.id || !currentOrganizationId) {
        if (isMounted) {
          setLoading(false);
          setHasAccess(false);
          setIsOwner(false);
        }
        return;
      }

      try {
        setLoading(true);

        const fallbackFeatureKey = FEATURE_FALLBACKS[featureKey];
        const organizationFeatureSelect = [featureKey];
        if (fallbackFeatureKey && fallbackFeatureKey !== featureKey) {
          organizationFeatureSelect.push(fallbackFeatureKey);
        }

        const {
          data: membershipData,
          error: membershipError,
        } = await supabase
          .from('organization_users')
          .select('role')
          .eq('user_id', user.id)
          .eq('organization_id', currentOrganizationId)
          .maybeSingle();

        if (membershipError) throw membershipError;

        const membershipRecord = membershipData as Record<string, unknown> | null;

        const {
          data: organizationFeatures,
          error: organizationFeaturesError,
        } = await supabase
          .from('organization_features')
          .select(organizationFeatureSelect.join(', '))
          .eq('organization_id', currentOrganizationId)
          .maybeSingle();

        let organizationFeatureRecord = organizationFeatures as Record<string, unknown> | null;
        let primaryOrganizationColumnAvailable = true;

        if (organizationFeaturesError) {
          if (isMissingColumnError(organizationFeaturesError) && fallbackFeatureKey && fallbackFeatureKey !== featureKey) {
            primaryOrganizationColumnAvailable = false;
            const { data: fallbackOrganizationFeatures, error: fallbackOrganizationError } = await supabase
              .from('organization_features')
              .select(fallbackFeatureKey)
              .eq('organization_id', currentOrganizationId)
              .maybeSingle();

            if (fallbackOrganizationError) {
              throw fallbackOrganizationError;
            }

            organizationFeatureRecord = fallbackOrganizationFeatures as Record<string, unknown> | null;
          } else if (isMissingColumnError(organizationFeaturesError)) {
            primaryOrganizationColumnAvailable = false;
            organizationFeatureRecord = null;
          } else {
            throw organizationFeaturesError;
          }
        }

        let organizationFeatureValue: boolean | null = null;

        if (primaryOrganizationColumnAvailable) {
          organizationFeatureValue = interpretFeatureFlag(organizationFeatureRecord?.[featureKey]);
        }

        if (organizationFeatureValue === null && fallbackFeatureKey) {
          organizationFeatureValue = interpretFeatureFlag(organizationFeatureRecord?.[fallbackFeatureKey]);
        }

        const organizationHasFeature = organizationFeatureValue ?? false;
        const membershipRole = (membershipRecord?.role ?? '').toString().toLowerCase();
        const owner = membershipRole === 'owner';

        if (isMounted) {
          setIsOwner(owner);
          setHasAccess(organizationHasFeature);
        }
      } catch (err) {
      console.error('Fehler beim Prüfen des Funktionszugriffs:', err);
        if (isMounted) {
          setHasAccess(false);
          setIsOwner(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    checkAccess(organizationId);

    return () => {
      isMounted = false;
    };
  }, [featureDisabled, featureKey, organizationId, supabase, user?.id]);

  return useMemo(
    () => ({
      loading,
      hasAccess,
      organizationId,
      isOwner,
    }),
    [hasAccess, isOwner, loading, organizationId],
  );
}

export default useFeatureAccess;

